import * as React from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { Check } from "lucide-react";

import { cn } from "../lib/cn";

/**
 * RadioGroup — shadcn/Radix radio primitive, Ando-styled.
 *
 * Headless: variants stay in the consumer. The wrapper just gives a
 * sensible default (vertical stack with `gap-1`) and a token-aligned
 * indicator. Used by ``SourceConfirmationCard`` (May 22, 2026) and
 * intended for any future agentic-confirm card with a candidate picker.
 *
 * Keyboard: Radix RadioGroup already handles ↑/↓, Home/End, Tab, and
 * roving tabindex per WAI-ARIA radio pattern. Just compose.
 */

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => (
  <RadioGroupPrimitive.Root
    ref={ref}
    className={cn("flex flex-col gap-1", className)}
    {...props}
  />
));
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => (
  <RadioGroupPrimitive.Item
    ref={ref}
    className={cn(
      // Token-aligned circle. Ando-orange checked state to match the
      // chrome-stripe signature of the agentic-confirm cards.
      "aspect-square h-4 w-4 shrink-0 rounded-full border border-ink-3/40",
      "text-ando-orange",
      "ring-offset-background",
      "focus:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45 focus-visible:ring-offset-2",
      "disabled:cursor-not-allowed disabled:opacity-50",
      "data-[state=checked]:border-ando-orange data-[state=checked]:bg-ando-orange",
      "transition-colors",
      className,
    )}
    {...props}
  >
    <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
      <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
    </RadioGroupPrimitive.Indicator>
  </RadioGroupPrimitive.Item>
));
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

/**
 * Headless re-exports of the underlying Radix primitive so consumers can
 * compose roving-tabindex icon rows (e.g. `<ChartTypeOverrideRow>`) without
 * shipping the default circle-check Indicator from `<RadioGroupItem>`.
 *
 * Use these when the radio "button" itself IS the visual (icon-only
 * toolbar). Otherwise prefer `<RadioGroup>` + `<RadioGroupItem>`.
 */
const RadioGroupHeadlessRoot = RadioGroupPrimitive.Root;
const RadioGroupHeadlessItem = RadioGroupPrimitive.Item;

export {
  RadioGroup,
  RadioGroupItem,
  RadioGroupHeadlessRoot,
  RadioGroupHeadlessItem,
};
